import { useState } from "react";
import { Eraser, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";

/** Emptying a unit's register before its first fill — the two SDTD administrators only. */
export const FlushUnit = ({ units = [], plantNames = {}, onDone }) => {
  const [unit, setUnit] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = async (value) => {
    setUnit(value);
    setConfirm("");
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/flush/${value}`);
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const flush = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/flush/${unit}`, { confirm });
      toast.success(
        `${unit} cleared — ${data.removed.use_cases} use case(s) and ${data.removed.readings} ` +
          "reading(s) removed. The team list, training records and accounts are untouched.",
      );
      setPreview(data.now);
      setConfirm("");
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!units.length) return null;

  return (
    <div
      className="mt-8 border border-[hsl(var(--at-risk)/0.5)] bg-card p-5"
      data-testid="flush-unit"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--at-risk))]" />
        <div className="max-w-3xl">
          <div className="label-caps text-[hsl(var(--at-risk))]">
            Administrator only · before the first fill
          </div>
          <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
            Clear a unit's register
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Empties the unit of its use cases, their journeys, their milestone dates and their
            compliance readings, so the unit can make a clean first fill from its own workbook. The
            team list, training records, accounts and the stored workbooks are left exactly as they
            are, and the clearance itself stays on the activity trail.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Select value={unit} onValueChange={pick}>
          <SelectTrigger className="h-11 w-[220px] rounded-none" data-testid="flush-unit-select">
            <SelectValue placeholder="Choose the unit" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            {units.map((u) => (
              <SelectItem key={u} value={u}>
                {u} — {plantNames[u] || u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="h-11 w-[190px] rounded-none font-data"
          placeholder={unit ? `Type ${unit} to confirm` : "Choose a unit first"}
          data-testid="flush-confirm-input"
          disabled={!unit}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button
          className="h-11 rounded-none bg-[hsl(var(--at-risk))] font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-[hsl(var(--at-risk)/0.85)]"
          data-testid="flush-submit"
          disabled={busy || !unit || confirm.trim().toUpperCase() !== unit.toUpperCase()}
          onClick={flush}
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Eraser className="mr-2 h-4 w-4" />
          )}
          Clear {unit || "unit"}
        </Button>
      </div>

      {preview && (
        <div
          className="mt-4 grid gap-px bg-border sm:grid-cols-3 lg:grid-cols-6"
          data-testid="flush-preview"
        >
          {[
            ["Use cases", preview.use_cases, true],
            ["Trial readings", preview.readings, true],
            ["Compliance snapshots", preview.snapshots, true],
            ["Waiting for approval", preview.pending_edits, true],
            ["Team members (kept)", preview.team_members, false],
            ["Learners (kept)", preview.learners, false],
          ].map(([label, value, goes]) => (
            <div key={label} className="bg-card px-4 py-3">
              <div
                className="font-data text-lg font-bold"
                style={goes ? { color: "hsl(var(--at-risk))" } : undefined}
              >
                {value ?? 0}
              </div>
              <div className="font-data mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
              </div>
            </div>
          ))}
        </div>
      )}
      {preview && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {preview.first_fill_done
            ? `${preview.unit} has already made its first fill — clearing it opens every column of its sheet again.`
            : `${preview.unit} is still on its first fill.`}
        </p>
      )}
    </div>
  );
};
